import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);
const USER_KEY = "prismiq_user";

/**
 * Auth state.
 *  - JWT lives ONLY in the httpOnly `prismiq_session` cookie set by the backend
 *    (login/register/reset-password). JS cannot read it → XSS-token-theft closed.
 *  - The non-sensitive user record (name/role/email/etc.) is cached in
 *    localStorage under `prismiq_user` purely to render UI shell on first paint;
 *    every mount re-validates via /auth/me which authenticates from the cookie.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const persistUser = (u) => {
    try {
      if (u) localStorage.setItem(USER_KEY, JSON.stringify(u));
      else localStorage.removeItem(USER_KEY);
    } catch (e) {
      console.warn("[auth] failed to persist user:", e?.message || e);
    }
  };

  const refresh = useCallback(async () => {
    // Skip the eager /auth/me probe on public entry pages — the 401 that would
    // otherwise appear in the browser console is misleading noise for guests.
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const publicRoute = /^(\/|\/login|\/register|\/forgot-password|\/reset-password|\/verify-email)(\/|$|\?)/.test(path);
    if (publicRoute && !user) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      persistUser(data);
    } catch {
      persistUser(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Cookie was set by the server. We just cache the user for the UI shell.
      persistUser(data.user);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (err) {
      return { ok: false, error: formatApiError(err) };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      persistUser(data.user);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (err) {
      return { ok: false, error: formatApiError(err) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("[auth] logout call failed:", e?.message || e);
    }
    persistUser(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
